import dayjs from "dayjs";
import { fetchFeed, toRows, pick, num, isIndexRow, indexNameOf, pageIndexName } from "./feeds.js";

// Growth of $10,000: Sequoia ETF vs. the S&P 500 Index, drawn as an inline SVG.
// Values come from the get_growth feed (Ultimus "growth of 10K" rows, as used by the
// Monarch site): one row per month-end per series, with endDate + noLoadAmount. The
// fund's rows carry portfolioNumber == fund id, its market-price twin uses id + "M",
// and each benchmark is a separate portfolioNumber whose portfolioName is the index
// name. Until the feed returns rows the chart stays blank (no placeholder lines).

const SEQ = "#003237";
const SP = "#8fbb9b";
const ML = 80, MR = 104, MT = 26, MB = 44, W = 1000, H = 440, PW = W - ML - MR, PH = H - MT - MB;

const PERIODS = {
    all: { label: "since inception (July 15, 1970)", start: "1970-07-15" },
    2016: { label: "since June 2016", start: "2016-06-01" },
    5: { label: "over the past 5 years", years: 5 },
    10: { label: "over the past 10 years", years: 10 },
    15: { label: "over the past 15 years", years: 15 },
    20: { label: "over the past 20 years", years: 20 },
};

let live = null; // { points: [{date, fund, index}] } once the feed delivers

export async function getGrowth(fundID) {
    wireChips();

    const data = await fetchFeed("get_growth", fundID);
    const points = toPoints(data, fundID);
    if (points.length >= 2) {
        live = { points: points };
        const first = points[0].date, last = points[points.length - 1].date;
        $("#growthRange").text(first.format("MMMM D, YYYY") + " – " + last.format("MMMM D, YYYY"));
        render(activePeriod());
    } else if (data) {
        console.warn("[feed] get_growth returned data in a shape growth.js does not recognise:", data);
    }
}

// Ultimus rows (portfolioNumber / portfolioName / endDate / noLoadAmount) are the expected
// shape. Wide rows ({date, fund, index}) and indexName-style long rows are still accepted.
function toPoints(data, fundID) {
    const rows = toRows(data);
    if (!rows.length) return [];
    const dateKeys = ["endDate", "performDate", "asOfDate", "date", "navDate", "valueDate", "periodEnd"];
    const fundKeys = ["noLoadAmount", "loadedAmount", "fundValue", "fund", "growthValue", "growth", "value", "nav", "naV_NoLoad", "amount"];
    const indexKeys = ["indexValue", "benchmarkValue", "sp500", "spValue", "index", "benchmark"];
    const wantIndex = pageIndexName();
    const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");

    if (rows.some((r) => r.portfolioNumber != null)) {
        const id = String(fundID);
        const isFund = (r) => String(r.portfolioNumber) === id || (norm(r.classId) === "fund" && String(r.assignedToPortfolioId || id) === id);
        const isMarket = (r) => /m$/i.test(String(r.portfolioNumber)) && String(r.portfolioNumber).slice(0, -1) === id || /market price/i.test(String(r.portfolioName));
        const isWantedIndex = (r) => !isFund(r) && !isMarket(r) && norm(r.portfolioName).indexOf(norm(wantIndex)) > -1;
        let indexRows = rows.filter(isWantedIndex);
        if (!indexRows.length) {
            // No name match: take the first benchmark series that is neither the fund nor its market-price twin.
            const other = rows.filter((r) => !isFund(r) && !isMarket(r));
            const firstId = other.length ? String(other[0].portfolioNumber) : null;
            indexRows = other.filter((r) => String(r.portfolioNumber) === firstId);
        }
        const byDate = {};
        const put = (r, field) => {
            const d = dayjs(pick(r, dateKeys));
            const v = num(pick(r, fundKeys));
            if (!d.isValid() || v == null) return;
            const key = d.format("YYYY-MM-DD");
            byDate[key] = byDate[key] || { date: d, fund: null, index: null };
            byDate[key][field] = v;
        };
        rows.filter(isFund).forEach((r) => put(r, "fund"));
        indexRows.forEach((r) => put(r, "index"));
        return finish(Object.keys(byDate).sort().map((k) => byDate[k]));
    }

    if (rows.some(isIndexRow)) {
        const byDate = {};
        rows.forEach((r) => {
            const d = dayjs(pick(r, dateKeys));
            const v = num(pick(r, fundKeys));
            if (!d.isValid() || v == null) return;
            const key = d.format("YYYY-MM-DD");
            byDate[key] = byDate[key] || { date: d, fund: null, index: null };
            if (!isIndexRow(r)) byDate[key].fund = v;
            else if (byDate[key].index == null || String(indexNameOf(r)).toLowerCase().indexOf(wantIndex.toLowerCase()) > -1) byDate[key].index = v;
        });
        return finish(Object.keys(byDate).sort().map((k) => byDate[k]));
    }

    return finish(
        rows
            .map((r) => {
                const d = dayjs(pick(r, dateKeys));
                return { date: d, fund: num(pick(r, fundKeys)), index: num(pick(r, indexKeys)) };
            })
            .filter((p) => p.date.isValid())
            .sort((a, b) => a.date.valueOf() - b.date.valueOf())
    );
}

function finish(points) {
    return points.filter((p) => p.fund != null && p.fund > 0);
}

function activePeriod() {
    const btn = document.querySelector(".chart-controls .chip.active");
    return btn ? btn.getAttribute("data-period") : "all";
}

function wireChips() {
    const chips = Array.prototype.slice.call(document.querySelectorAll(".chart-controls .chip"));
    chips.forEach((btn) => {
        btn.addEventListener("click", () => {
            chips.forEach((b) => { b.classList.remove("active"); b.setAttribute("aria-pressed", "false"); });
            btn.classList.add("active");
            btn.setAttribute("aria-pressed", "true");
            render(btn.getAttribute("data-period"));
        });
    });
}

function render(key) {
    if (!live) return;
    const p = PERIODS[key] || PERIODS.all;
    const model = liveSeries(p);
    if (!model) return;
    draw(model);
    document.getElementById("growthNote").innerHTML =
        "Growth of $10,000 " + p.label + ", assuming reinvestment of dividends. The S&amp;P 500 Index is unmanaged and incurs no expenses; it is not possible to invest directly in an index. As of " + model.endLabel + ".";
}

// Rebase both series to $10,000 at the start of the selected period.
function liveSeries(p) {
    const all = live.points;
    const last = all[all.length - 1].date;
    const from = p.start ? dayjs(p.start) : last.subtract(p.years, "year");
    const slice = all.filter((pt) => !pt.date.isBefore(from));
    if (slice.length < 2) return null;
    const f0 = slice[0].fund;
    const firstIndex = slice.find((pt) => pt.index != null && pt.index > 0);
    const i0 = firstIndex ? firstIndex.index : null;
    const seq = slice.map((pt) => (pt.fund / f0) * 10000);
    const sp = i0 ? slice.map((pt) => (pt.index != null ? (pt.index / i0) * 10000 : null)) : null;
    const vals = seq.concat(sp ? sp.filter((v) => v != null) : []);
    const spread = Math.max.apply(null, vals) / Math.min.apply(null, vals);
    return {
        seq: seq,
        sp: sp,
        startY: slice[0].date.year() + slice[0].date.diff(slice[0].date.startOf("year"), "day") / 366,
        years: Math.max(last.diff(slice[0].date, "day") / 365.25, 0.1),
        // Log scale only when the values span more than a decade of growth (the full 1970s-on history).
        log: spread > 10,
        endLabel: last.format("MMMM D, YYYY"),
    };
}

// ---- SVG drawing ----
function niceNum(x) {
    const exp = Math.floor(Math.log10(x)), f = x / Math.pow(10, exp);
    const nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
    return nf * Math.pow(10, exp);
}
function moneyLabel(v) {
    return v >= 1e6 ? "$" + (v / 1e6).toFixed(2) + "M" : "$" + Math.round(v).toLocaleString("en-US");
}
function axisLabel(v) {
    if (v >= 1e6) return "$" + (v / 1e6).toFixed(v >= 1e7 ? 0 : 1).replace(/\.0$/, "") + "M";
    if (v >= 1e3) return "$" + v / 1e3 + "K";
    return "$" + v;
}

function draw(model) {
    const seq = model.seq, sp = model.sp, N = seq.length - 1;
    const values = seq.concat(sp ? sp.filter((v) => v != null) : []);
    const dmin = Math.min.apply(null, values), dmax = Math.max.apply(null, values);
    const svg = [];
    let yTo;
    const ticks = [];

    if (model.log) {
        const lo = Math.min(4, Math.floor(Math.log10(dmin))), hi = Math.log10(dmax) + 0.03;
        yTo = (v) => MT + PH - ((Math.log10(v) - lo) / (hi - lo)) * PH;
        for (let i = Math.ceil(lo); i <= Math.floor(hi); i++) ticks.push(Math.pow(10, i));
    } else {
        const step = niceNum((dmax - dmin) / 4 || 1);
        const aLo = Math.floor(dmin / step) * step, aHi = Math.ceil(dmax / step) * step;
        yTo = (v) => MT + PH - ((v - aLo) / (aHi - aLo)) * PH;
        for (let t = aLo; t <= aHi + 1e-6; t += step) ticks.push(t);
    }
    const xTo = (idx) => ML + (idx / N) * PW;

    svg.push('<g font-family="ingra, Source Sans 3, sans-serif" font-size="12" fill="#8a938b">');
    ticks.forEach((v) => {
        const y = yTo(v);
        svg.push('<line x1="' + ML + '" y1="' + y.toFixed(1) + '" x2="' + (W - MR) + '" y2="' + y.toFixed(1) + '" stroke="#e6eae4"/>');
        svg.push('<text x="' + (ML - 10) + '" y="' + (y + 4).toFixed(1) + '" font-size="9" text-anchor="end">' + axisLabel(v) + "</text>");
    });
    const nX = 5;
    for (let i = 0; i <= nX; i++) {
        const idx = Math.round((i / nX) * N), yr = Math.floor(model.startY + (idx / N) * model.years + 1e-6);
        const anchor = i === 0 ? "start" : i === nX ? "end" : "middle";
        svg.push('<text x="' + xTo(idx).toFixed(1) + '" y="' + (H - 16) + '" font-size="9" text-anchor="' + anchor + '">' + yr + "</text>");
    }
    svg.push("</g>");
    svg.push('<line x1="' + ML + '" y1="' + (MT + PH) + '" x2="' + (W - MR) + '" y2="' + (MT + PH) + '" stroke="#cfd6cf"/>');

    const path = (arr) => {
        let d = "", pen = false;
        for (let k = 0; k < arr.length; k++) {
            if (arr[k] == null) { pen = false; continue; }
            d += (pen ? "L" : "M") + xTo(k).toFixed(1) + "," + yTo(arr[k]).toFixed(1) + " ";
            pen = true;
        }
        return d.trim();
    };
    if (sp) svg.push('<path d="' + path(sp) + '" fill="none" stroke="' + SP + '" stroke-width="2.5" stroke-linejoin="round"/>');
    svg.push('<path d="' + path(seq) + '" fill="none" stroke="' + SEQ + '" stroke-width="3" stroke-linejoin="round"/>');

    const ex = xTo(N);
    const items = [{ vy: yTo(seq[N]), text: moneyLabel(seq[N]), dot: SEQ, tc: SEQ, w: 700 }];
    if (sp && sp[N] != null) items.push({ vy: yTo(sp[N]), text: moneyLabel(sp[N]), dot: SP, tc: "#5f7d6f", w: 600 });
    items.forEach((it) => svg.push('<circle cx="' + ex.toFixed(1) + '" cy="' + it.vy.toFixed(1) + '" r="4" fill="' + it.dot + '"/>'));
    const sorted = items.slice().sort((a, b) => a.vy - b.vy), gap = 15;
    sorted[0].ly = sorted[0].vy;
    for (let j = 1; j < sorted.length; j++) sorted[j].ly = Math.max(sorted[j].vy, sorted[j - 1].ly + gap);
    const overflow = sorted[sorted.length - 1].ly - (MT + PH);
    if (overflow > 0) sorted.forEach((it) => (it.ly -= overflow));
    items.forEach((it) => {
        svg.push('<text x="' + (ex + 8) + '" y="' + (it.ly + 4).toFixed(1) + '" font-family="ingra, Source Sans 3, sans-serif" font-size="13" font-weight="' + it.w + '" fill="' + it.tc + '">' + it.text + "</text>");
    });

    document.getElementById("growthChart").innerHTML = svg.join("");
}
